// external modules
import { mapState, mapActions } from 'vuex'

// internal modules
import Storage from '@/utils/Storage'
import '@/assets/scss/intro.scss'

// components
import BaseIntroReset from '@/components/BaseIntroReset.vue'

export default {
  inject: ['intro_js'],
  components: {
    BaseIntroReset,
  },
  data: {
    intro_timeout: null,
  },
  computed: {
    ...mapState({
      $_intro_key: (state) => state.dashboard.intro.key,
      $_intro_type: (state) => state.dashboard.intro.type,
      $_intro_status: (state) => state.dashboard.intro.status,
    }),
    intro_key: {
      get() {
        return this.$_intro_key
      },
      set(payload) {
        this.setIntroKey(payload)
      },
    },
    intro_type: {
      get() {
        return this.$_intro_type
      },
      set(payload) {
        this.setIntroType(payload)
      },
    },
    intro_status: {
      get() {
        return this.$_intro_status
      },
      set(payload) {
        this.setIntroStatus(payload)
      },
    },
    introJS() {
      return this.intro_js
    },
  },
  beforeDestroy() {
    this.intro_key = null
    this.intro_type = 'intro'
    this.intro_status = 'finished'

    this.hideIntroJS()
  },
  methods: {
    ...mapActions('dashboard', [
      'setIntroKey',
      'setIntroType',
      'setIntroStatus',
    ]),
    /**
     * Initialize intro for the current page
     *
     * @param {string} key index to identify the page
     * @param {number} delay delay time
     * @param {string} type intro type: 'hint' or 'intro'. Default 'intro'
     * @param {boolean} reset bypass key saved on localStorage
     * @returns void
     */
    setIntroJS({ key, delay = 10000, type = 'intro', reset = false }) {
      const prop = `introjs-${key}-finished`

      const hasFinished = reset ? false : Storage.getItem(prop)
      const isNotValidKey = !key
      const isHint = type === 'hint'

      this.intro_key = key
      this.intro_type = type

      if (isNotValidKey || hasFinished) return

      this.hideIntroJS()

      this.intro_timeout = setTimeout(() => {
        this.intro_status = isHint ? 'finished' : 'started'

        if (!isHint) document.body.classList.add('intro-started')

        if (!isHint) return this.startIntros(prop)

        if (reset) return this.resetHints(prop)

        this.startHints(prop)
      }, delay)
    },
    getHintOptions() {
      const i18n = this.$i18next
      const path = 'message.introJS.options'

      return {
        hintButtonLabel: i18n.t(`${path}.hintButtonLabel`),
        hintPosition: 'top-left',
      }
    },
    getIntroOptions() {
      const i18n = this.$i18next
      const path = 'message.introJS.options'

      return {
        nextLabel: i18n.t(`${path}.nextLabel`),
        prevLabel: i18n.t(`${path}.prevLabel`),
        doneLabel: i18n.t(`${path}.doneLabel`),
        stepNumbersOfLabel: i18n.t(`${path}.stepNumbersOfLabel`),
        disableInteraction: true,
      }
    },
    startHints(prop) {
      const options = this.getHintOptions()

      this.introJS()
        .setOptions(options)
        .onhintclose(() => this.saveToStorage(prop))
        .addHints()
    },
    resetHints(prop) {
      const options = this.getHintOptions()

      this.introJS()
        .setOptions(options)
        .onhintclose(() => this.saveToStorage(prop))
        .showHints()
    },
    startIntros(prop) {
      const options = this.getIntroOptions()

      this.introJS()
        .setOptions(options)
        .oncomplete(() => this.saveToStorage(prop))
        .onexit(() => this.saveToStorage(prop))
        .start()
    },
    hideIntroJS() {
      clearTimeout(this.intro_timeout)

      this.introJS().removeHints()
      this.introJS().exit()
    },
    saveToStorage(prop) {
      Storage.setItem(prop, true)
      document.body.classList.remove('intro-started')
      this.intro_status = 'finished'
    },
    refreshIntro() {
      this.setIntroJS({
        key: this.intro_key,
        type: this.intro_type,
        delay: 350,
        reset: true,
      })
    },
  },
}
